import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { 
  ArrowLeft, ArrowRight, CheckCircle, XCircle, HelpCircle,
  Clock, Award, AlertTriangle, ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const StudentQuizPage = () => {
  const { courseId, quizId } = useParams();
  const navigate = useNavigate();
  
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [results, setResults] = useState(null);
  const [startTime] = useState(Date.now());
  
  const fetchQuiz = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/content/quizzes/${quizId}`);
      setQuiz(response.data);
    } catch (error) {
      console.error('Error fetching quiz:', error);
      toast.error('Erreur lors du chargement du quiz');
    } finally {
      setLoading(false);
    }
  }, [quizId]);
  
  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);
  
  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const isMultiChecked = (questionId, option) => {
    try {
      const current = answers[questionId] ? JSON.parse(answers[questionId]) : [];
      return Array.isArray(current) && current.includes(option);
    } catch {
      return false;
    }
  };

  const handleMultiAnswer = (questionId, option) => {
    setAnswers(prev => {
      let current = [];
      try {
        current = prev[questionId] ? JSON.parse(prev[questionId]) : [];
        if (!Array.isArray(current)) current = [];
      } catch {
        current = [];
      }
      const next = current.includes(option)
        ? current.filter(o => o !== option)
        : [...current, option];
      const updated = { ...prev };
      if (next.length) {
        updated[questionId] = JSON.stringify(next.slice().sort());
      } else {
        delete updated[questionId];
      }
      return updated;
    });
  };
  
  const submitQuiz = async () => {
    // Check if all questions are answered
    const unanswered = quiz.questions.filter(q => !answers[q.id]);
    if (unanswered.length > 0) {
      toast.error(`Veuillez répondre à toutes les questions (${unanswered.length} restantes)`);
      return;
    }
    
    setSubmitting(true);
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 60000);
      
      const response = await api.post(`/content/quizzes/${quizId}/submit`, {
        answers: Object.entries(answers).map(([questionId, answer]) => ({
          question_id: parseInt(questionId),
          answer: answer
        })),
        time_spent_minutes: timeSpent
      });
      
      setResults(response.data);
      setSubmitted(true);
      
      if (response.data.passed) {
        toast.success('Félicitations ! Vous avez réussi le quiz !');
      } else {
        toast.info('Quiz terminé. Consultez vos résultats.');
      }
    } catch (error) {
      console.error('Error submitting quiz:', error);
      toast.error('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };
  
  const goToQuestion = (index) => {
    if (index >= 0 && index < quiz.questions.length) {
      setCurrentQuestion(index);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <HelpCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
          <p className="text-slate-600">Quiz non trouvé</p>
          <Button onClick={() => navigate(`/student/courses/${courseId}`)} className="mt-4">
            Retour au cours
          </Button>
        </div>
      </div>
    );
  }

  // Results View
  if (submitted && results) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-4">
          <div className="max-w-3xl mx-auto flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(`/student/courses/${courseId}`)}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-semibold text-slate-900 dark:text-white">
              Résultats du quiz
            </h1>
          </div>
        </header>
        
        <main className="max-w-3xl mx-auto px-4 py-8">
          {/* Score Card */}
          <Card className={`mb-8 ${results.passed ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
            <CardContent className="p-8 text-center">
              <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                results.passed ? 'bg-emerald-100' : 'bg-amber-100'
              }`}>
                {results.passed ? (
                  <Award className="w-10 h-10 text-emerald-600" />
                ) : (
                  <AlertTriangle className="w-10 h-10 text-amber-600" />
                )}
              </div>
              
              <h2 className={`text-2xl font-bold mb-2 ${
                results.passed ? 'text-emerald-800' : 'text-amber-800'
              }`}>
                {results.passed ? 'Félicitations !' : 'Quiz terminé'}
              </h2>
              
              <div className={`text-5xl font-bold mb-2 ${
                results.passed ? 'text-emerald-600' : 'text-amber-600'
              }`}>
                {results.score}%
              </div>
              
              <p className={`text-sm ${results.passed ? 'text-emerald-600' : 'text-amber-600'}`}>
                {results.correct_count}/{results.total_questions} réponses correctes
              </p>
              
              {!results.passed && quiz.passing_score && (
                <p className="text-sm text-amber-600 mt-2">
                  Score minimum requis: {quiz.passing_score}%
                </p>
              )}
            </CardContent>
          </Card>
          
          {/* Questions Review */}
          <Card>
            <CardHeader>
              <CardTitle>Revue des réponses</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {results.details?.map((detail, index) => (
                <div 
                  key={detail.question_id} 
                  className={`p-4 rounded-lg border ${
                    detail.is_correct 
                      ? 'border-emerald-200 bg-emerald-50' 
                      : 'border-red-200 bg-red-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      detail.is_correct ? 'bg-emerald-100' : 'bg-red-100'
                    }`}>
                      {detail.is_correct ? (
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900 mb-2">
                        {index + 1}. {detail.question_text}
                      </p>
                      
                      <div className="space-y-1 text-sm">
                        <p className={detail.is_correct ? 'text-emerald-700' : 'text-red-700'}>
                          Votre réponse: {detail.user_answer}
                        </p>
                        {!detail.is_correct && (
                          <p className="text-emerald-700">
                            Réponse correcte: {detail.correct_answer}
                          </p>
                        )}
                        {detail.explanation && (
                          <p className="text-slate-600 mt-2 p-2 bg-white rounded">
                            <strong>Explication:</strong> {detail.explanation}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
          
          {/* Actions */}
          <div className="mt-6 flex justify-center gap-4">
            <Button 
              variant="outline"
              onClick={() => navigate(`/student/courses/${courseId}`)}
            >
              Retour au cours
            </Button>
            {!results.passed && (
              <Button 
                className="bg-violet-600 hover:bg-violet-700"
                onClick={() => {
                  setSubmitted(false);
                  setResults(null);
                  setAnswers({});
                  setCurrentQuestion(0);
                }}
              >
                Réessayer
              </Button>
            )}
          </div>
        </main>
      </div>
    );
  }

  // Quiz View
  const question = quiz.questions[currentQuestion];
  const answeredCount = Object.keys(answers).length;
  const progressPercent = (answeredCount / quiz.questions.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-3">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(`/student/courses/${courseId}`)}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-semibold text-slate-900 dark:text-white">
                  {quiz.title}
                </h1>
                <p className="text-sm text-slate-500">
                  Question {currentQuestion + 1} sur {quiz.questions.length}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {quiz.duration_minutes && (
                <Badge variant="outline" className="gap-1">
                  <Clock className="w-3 h-3" />
                  {quiz.duration_minutes} min
                </Badge>
              )}
              <Badge className="bg-violet-100 text-violet-700">
                {answeredCount}/{quiz.questions.length} répondues
              </Badge>
            </div>
          </div>
          
          <Progress value={progressPercent} className="h-1" />
        </div>
      </header>

      {/* Question */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="p-6 lg:p-8">
            {/* Question Number & Type */}
            <div className="flex items-center gap-2 mb-4">
              <Badge className="bg-slate-100 text-slate-700">
                Question {currentQuestion + 1}
              </Badge>
              <Badge variant="outline">
                {question.question_type === 'multi_select' ? 'Choix multiple' :
                 question.question_type === 'true_false' ? 'Vrai/Faux' :
                 question.question_type === 'open' ? 'Question ouverte' : 'Choix unique'}
              </Badge>
              {question.points && (
                <Badge variant="outline">{question.points} pts</Badge>
              )}
            </div>
            
            {/* Question Text */}
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-6">
              {question.question_text}
            </h2>
            
            {/* Single-answer choice options */}
            {(question.question_type === 'single_choice' ||
              question.question_type === 'multiple_choice' ||
              question.question_type === 'choice' ||
              question.question_type === 'true_false') && (
              <RadioGroup
                value={answers[question.id] || ''}
                onValueChange={(value) => handleAnswer(question.id, value)}
                className="space-y-3"
              >
                {question.options?.map((option, index) => (
                  <label
                    key={index}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      answers[question.id] === option
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <RadioGroupItem value={option} id={`option-${index}`} />
                    <span className="text-slate-900 dark:text-white">{option}</span>
                  </label>
                ))}
              </RadioGroup>
            )}

            {/* Multiple-answer options (checkboxes) */}
            {question.question_type === 'multi_select' && (
              <div className="space-y-3">
                <p className="text-sm text-slate-500">Plusieurs réponses possibles</p>
                {question.options?.map((option, index) => (
                  <label
                    key={index}
                    className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                      isMultiChecked(question.id, option)
                        ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isMultiChecked(question.id, option)}
                      onChange={() => handleMultiAnswer(question.id, option)}
                      className="w-4 h-4 text-violet-600 border-slate-300 rounded focus:ring-violet-500"
                    />
                    <span className="text-slate-900 dark:text-white">{option}</span>
                  </label>
                ))}
              </div>
            )}
            
            {/* Open Question */}
            {question.question_type === 'open' && (
              <Textarea
                value={answers[question.id] || ''}
                onChange={(e) => handleAnswer(question.id, e.target.value)}
                placeholder="Écrivez votre réponse ici..."
                rows={6}
                className="resize-none"
              />
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => goToQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 0}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Précédent
          </Button>
          
          {/* Question Dots */}
          <div className="hidden md:flex items-center gap-1">
            {quiz.questions.map((q, index) => (
              <button
                key={q.id}
                onClick={() => goToQuestion(index)}
                className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                  index === currentQuestion
                    ? 'bg-violet-600 text-white'
                    : answers[q.id]
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          
          {currentQuestion < quiz.questions.length - 1 ? (
            <Button
              onClick={() => goToQuestion(currentQuestion + 1)}
            >
              Suivant
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={submitQuiz}
              disabled={submitting}
            >
              {submitting ? 'Envoi...' : 'Terminer le quiz'}
            </Button>
          )}
        </div>
        
        {/* Mobile Question Navigation */}
        <div className="md:hidden mt-4 flex flex-wrap gap-1 justify-center">
          {quiz.questions.map((q, index) => (
            <button
              key={q.id}
              onClick={() => goToQuestion(index)}
              className={`w-8 h-8 rounded-full text-xs font-medium transition-colors ${
                index === currentQuestion
                  ? 'bg-violet-600 text-white'
                  : answers[q.id]
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-600'
              }`}
            >
              {index + 1}
            </button>
          ))}
        </div>
      </main>
    </div>
  );
};

export default StudentQuizPage;
